import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import api from '../services/api';

const RegistrationModal = ({ isOpen, onClose, selectedDate, onSuccess, bookedDates = [] }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    description: '',
  });
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (selectedDate) {
      setStartDate(selectedDate);
      setEndDate(selectedDate);
      setCurrentMonth(new Date(selectedDate));
    }
  }, [selectedDate]);

  if (!isOpen || !selectedDate) return null;

  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('lt-LT', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatDateShort = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('lt-LT', {
      month: 'short',
      day: 'numeric',
    });
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1; // Monday = 0
    
    return { daysInMonth, startingDayOfWeek, year, month };
  };

  const isDateInRange = (date) => {
    if (!startDate || !endDate) return false;
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    const start = new Date(startDate);
    start.setHours(0, 0, 0, 0);
    const end = new Date(endDate);
    end.setHours(0, 0, 0, 0);
    return d >= start && d <= end;
  };

  // Helper to format date as YYYY-MM-DD string (local time, no timezone issues)
  const formatDateStr = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const isDateBooked = (date) => {
    const dateStr = formatDateStr(date);
    return bookedDates.some(d => {
      const bookedStr = formatDateStr(new Date(d));
      return bookedStr === dateStr;
    });
  };

  const isDatePast = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d < today;
  };

  const isSameDay = (date1, date2) => {
    if (!date1 || !date2) return false;
    const d1 = new Date(date1);
    const d2 = new Date(date2);
    return d1.toISOString().split('T')[0] === d2.toISOString().split('T')[0];
  };

  const handleDateClick = (day) => {
    const clickedDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    
    if (isDatePast(clickedDate) || isDateBooked(clickedDate)) return;

    if (!startDate || (startDate && endDate && !isSameDay(startDate, endDate))) {
      // Start new selection
      setStartDate(clickedDate);
      setEndDate(clickedDate);
    } else if (clickedDate < startDate) {
      // Clicked before start - set as new start
      setStartDate(clickedDate);
    } else if (clickedDate > startDate) {
      // Check if any date in range is booked
      let hasBookedDate = false;
      const tempDate = new Date(startDate);
      while (tempDate <= clickedDate) {
        if (isDateBooked(tempDate)) {
          hasBookedDate = true;
          break;
        }
        tempDate.setDate(tempDate.getDate() + 1);
      }
      
      if (hasBookedDate) {
        toast.error('Pasirinktas laikotarpis apima užimtas datas');
        return;
      }
      
      setEndDate(clickedDate);
    } else {
      // Same day clicked - keep single day
      setEndDate(clickedDate);
    }
  };

  const getNumberOfDays = () => {
    if (!startDate || !endDate) return 0;
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    return diffDays;
  };

  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Vardas yra privalomas';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'El. paštas yra privalomas';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Neteisingas el. pašto formatas';
    }
    
    if (!formData.phone.trim()) {
      newErrors.phone = 'Telefonas yra privalomas';
    }

    if (!startDate || !endDate) {
      newErrors.dates = 'Pasirinkite datas';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    
    try {
      // Use local date format to avoid timezone issues
      const startStr = formatDateStr(startDate);
      const endStr = formatDateStr(endDate);
      
      await api.post('/events/book', {
        reservation_date: startStr,
        end_date: endStr,
        customer_name: formData.name,
        customer_email: formData.email,
        customer_phone: formData.phone,
        description: formData.description || null,
      });
      
      toast.success('Rezervacijos užklausa išsiųsta! Administratorius peržiūrės ir patvirtins jūsų rezervaciją. Susisieksime su jumis netrukus.', { duration: 6000 });
      setFormData({ name: '', email: '', phone: '', description: '' });
      setStartDate(null);
      setEndDate(null);
      if (onSuccess) {
        onSuccess();
      } else {
        onClose();
      }
    } catch (error) {
      const message = error.response?.data?.message || 'Įvyko klaida. Bandykite dar kartą.';
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const renderCalendar = () => {
    const { daysInMonth, startingDayOfWeek, year, month } = getDaysInMonth(currentMonth);
    const days = [];
    const weekDays = ['Pr', 'An', 'Tr', 'Kt', 'Pn', 'Št', 'Sk'];

    // Empty cells for days before the first day of month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(<div key={`empty-${i}`} className="w-8 h-8"></div>);
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const isPast = isDatePast(date);
      const isBooked = isDateBooked(date);
      const isInRange = isDateInRange(date);
      const isStart = isSameDay(date, startDate);
      const isEnd = isSameDay(date, endDate);

      let className = 'w-8 h-8 text-sm rounded-full flex items-center justify-center transition-colors cursor-pointer ';
      
      if (isPast) {
        className += 'text-gray-300 cursor-not-allowed';
      } else if (isBooked) {
        className += 'bg-red-100 text-red-400 cursor-not-allowed';
      } else if (isStart || isEnd) {
        className += 'bg-[#D0AB64] text-black font-bold';
      } else if (isInRange) {
        className += 'bg-[#D0AB64] bg-opacity-30 text-black';
      } else {
        className += 'hover:bg-gray-100 text-gray-700';
      }

      days.push(
        <div
          key={day}
          className={className}
          onClick={() => handleDateClick(day)}
        >
          {day}
        </div>
      );
    }

    return (
      <div className="bg-gray-50 rounded-lg p-3">
        {/* Month navigation */}
        <div className="flex items-center justify-between mb-3">
          <button
            type="button"
            onClick={prevMonth}
            className="p-1 hover:bg-gray-200 rounded-full transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <span className="font-medium text-gray-900">
            {currentMonth.toLocaleDateString('lt-LT', { year: 'numeric', month: 'long' })}
          </span>
          <button
            type="button"
            onClick={nextMonth}
            className="p-1 hover:bg-gray-200 rounded-full transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        {/* Week days header */}
        <div className="grid grid-cols-7 gap-1 mb-2">
          {weekDays.map(day => (
            <div key={day} className="w-8 h-6 text-xs text-gray-500 flex items-center justify-center font-medium">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar days */}
        <div className="grid grid-cols-7 gap-1">
          {days}
        </div>

        {/* Legend */}
        <div className="flex items-center justify-center gap-4 mt-3 pt-3 border-t border-gray-200">
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded-full bg-[#D0AB64]"></div>
            <span className="text-xs text-gray-600">Pasirinkta</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded-full bg-red-100"></div>
            <span className="text-xs text-gray-600">Užimta</span>
          </div>
        </div>
      </div>
    );
  };

  const numberOfDays = getNumberOfDays();

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      onClick={handleOverlayClick}
    >
      <div 
        className="bg-white rounded-lg shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-6 border-b border-gray-100" style={{ backgroundColor: '#D0AB64' }}>
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-xl font-bold text-black">
                Rezervacija
              </h2>
              {startDate && endDate && (
                <p className="text-black text-sm mt-1 opacity-80">
                  {isSameDay(startDate, endDate) 
                    ? formatDate(startDate)
                    : `${formatDateShort(startDate)} - ${formatDateShort(endDate)}`
                  }
                  {numberOfDays > 1 && (
                    <span className="ml-2 px-2 py-0.5 bg-black bg-opacity-20 rounded-full text-xs font-medium">
                      {numberOfDays} {numberOfDays === 1 ? 'diena' : numberOfDays < 10 ? 'dienos' : 'dienų'}
                    </span>
                  )}
                </p>
              )}
            </div>
            <button
              onClick={onClose}
              className="p-2 text-black hover:bg-black hover:bg-opacity-10 rounded-lg transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <p className="text-gray-600 mb-4">
            Pasirinkite datas kalendoriuje ir užpildykite formą. Mes susisieksime su jumis patvirtinti rezervaciją.
          </p>

          {/* Mini Calendar */}
          <div className="mb-4">
            {renderCalendar()}
            {errors.dates && (
              <p className="mt-1 text-sm text-red-500">{errors.dates}</p>
            )}
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                Vardas ir pavardė *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#D0AB64] focus:border-transparent transition-colors ${
                  errors.name ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Jonas Jonaitis"
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-500">{errors.name}</p>
              )}
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                El. paštas *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#D0AB64] focus:border-transparent transition-colors ${
                  errors.email ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="jonas@pavyzdys.lt"
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-500">{errors.email}</p>
              )}
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                Telefonas *
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#D0AB64] focus:border-transparent transition-colors ${
                  errors.phone ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="+370 600 00000"
              />
              {errors.phone && (
                <p className="mt-1 text-sm text-red-500">{errors.phone}</p>
              )}
            </div>

            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                Papildoma informacija
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={3}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D0AB64] focus:border-transparent transition-colors resize-none"
                placeholder="Nurodykite asmenų skaičių, specialius pageidavimus ir kt."
              />
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full font-semibold py-3 px-6 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
              style={{ backgroundColor: '#D0AB64', color: '#111315' }}
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-black border-t-transparent"></div>
                  <span>Rezervuojama...</span>
                </>
              ) : (
                <span>
                  Rezervuoti {numberOfDays > 1 ? `(${numberOfDays} dienos)` : ''}
                </span>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default RegistrationModal;
